
    // IMPORTS
    import { computed, defineComponent, ref } from 'vue'
    import { useRoute } from 'vue-router'
    import router from '@/router'
    import { useSessionStore } from '@/store/session'

    import { Input } from '@progress/kendo-vue-inputs'
    import { Button } from '@progress/kendo-vue-buttons'
    import { Error } from '@progress/kendo-vue-labels'
    import Loader from '@/components/common/loader.vue'
    import RequestAccountDialog from '@/components/auth/request-account-dialog.vue'
    import RequestAccountResultDialog from '@/components/auth/request-account-result-dialog.vue'

    import { AuthenticationApi, buildLocalization, DEBUG, StringUtil } from '@/util'
    import { AxiosError, AxiosResponse, HttpStatusCode } from 'axios'
    import { ILoginResultDTO } from '@/types/auth'

    export default defineComponent({
        name: 'PartnerLoginForm',

        components: {
            Loader,
            RequestAccountDialog,
            RequestAccountResultDialog,
            'k-input': Input,
            'k-button': Button,
            Error
        },

        setup() {
            const DEFAULT_REDIRECT = '/partner'

            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/
            const { localize } = buildLocalization()
            const route = useRoute()

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const username = ref('')
            const password = ref('')
            const usernameLoginAttempt = ref(false)
            const passwordLoginAttempt = ref(false)
            const loginInProgress = ref(false)
            const loginErrorMessage = ref('')
            const showRequestAccountDialog = ref(false)
            const requestAccountResult = ref<boolean | null>(null)
            const refUsername = ref<InstanceType<typeof Input> | null>(null)
            const refPassword = ref<InstanceType<typeof Input> | null>(null)

            const publicData = {
                username, password, loginInProgress, loginErrorMessage,
                showRequestAccountDialog, requestAccountResult, refUsername, refPassword
            }

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const returnUrl = computed(() => route.query.return as string | undefined)
            const usernameHasContent = computed(() => StringUtil.hasContent(username.value))
            const passwordHasContent = computed(() => StringUtil.hasContent(password.value))
            const showUsernameError = computed(() => !usernameHasContent.value && usernameLoginAttempt.value)
            const showPasswordError = computed(() => !passwordHasContent.value && passwordLoginAttempt.value)

            const computedData = { showUsernameError, showPasswordError }

            /********************************************************************************************************************
             PUBLIC METHODS
             ********************************************************************************************************************/
            function login() {
                usernameLoginAttempt.value = true
                passwordLoginAttempt.value = true
                if (usernameHasContent.value && passwordHasContent.value) {
                    getUserToken()
                }
            }

            function onEnter(source: string) {
                switch (source) {
                    case 'username':
                        if (usernameHasContent.value) {
                            refPassword.value?.focus?.()
                        } else {
                            usernameLoginAttempt.value = true
                        }
                        break
                    case 'password':
                        login()
                        break
                }
            }

            function onUsernameInput() {
                usernameLoginAttempt.value = false
                loginErrorMessage.value = ''
            }

            function onPasswordInput() {
                passwordLoginAttempt.value = false
                loginErrorMessage.value = ''
            }

            function onRequestAccountDone(success: boolean) {
                showRequestAccountDialog.value = false
                requestAccountResult.value = success
            }

            const publicMethods = { localize, login, onEnter, onUsernameInput, onPasswordInput, onRequestAccountDone }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function getUserToken() {
                loginInProgress.value = true
                const api = new AuthenticationApi()

                api.getUserToken({ username: username.value, password: password.value })
                    .then(response => handleResponse(response))
                    .catch(err => {
                        if (err instanceof AxiosError && err.response != null) {
                            handleResponse(err.response)
                        }
                    })
                    .finally(() => loginInProgress.value = false)
            }

            function handleResponse(response: AxiosResponse<ILoginResultDTO>) {
                const sessionStore = useSessionStore()
                loginErrorMessage.value = ''

                switch (response.status) {
                    case HttpStatusCode.Ok:
                        sessionStore.setPartnerLogin(response.data)
                        if (returnUrl.value) {
                            DEBUG(`Redirecting to ${returnUrl.value}...`)
                            router.replace(returnUrl.value)
                        } else {
                            DEBUG(`Redirecting to ${DEFAULT_REDIRECT}...`)
                            router.push(DEFAULT_REDIRECT)
                        }
                        break
                    case HttpStatusCode.Unauthorized:
                        loginErrorMessage.value = localize('InvalidCredentials')
                        break
                    case HttpStatusCode.Forbidden:
                        loginErrorMessage.value = localize('AccessBlocked')
                        break
                    default:
                        loginErrorMessage.value = localize('UnexpectedError')
                        break
                }
            }

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    })
